import React, { useEffect, useState, memo } from "react";
import PropTypes from "prop-types";
import { Spinner, Card } from "react-bootstrap";
import { ReactComponent as EditIcon } from "../Assets/Images/edit.svg";
import { ReactComponent as RemoveIcon } from "../Assets/Images/bin.svg";
import { validateUrlImage } from "../helpers/menu";
import Storage from "@aws-amplify/storage";

const PreviewMenuCard = memo(
  ({
    name,
    price,
    format,
    vintage,
    imageUrl, // Can be S3-image-key
    menuItemIndex,
    handleChangeImage,
    handleRemoveImage,
    normalText,
    subtext,
  }) => {
    const textStyle = React.useCallback((styleSettingObj) => {
      return {
        ...(styleSettingObj?.font
          ? { fontFamily: styleSettingObj?.font }
          : { fontFamily: "Libre Baskerville" }),
        ...(styleSettingObj?.color
          ? { color: styleSettingObj?.color }
          : { color: "#000" }),
        ...(styleSettingObj?.textstyle &&
          styleSettingObj?.textstyle.includes("bold") && {
            fontWeight: "bold",
          }),
        ...(styleSettingObj?.textstyle &&
          styleSettingObj?.textstyle.includes("italic") && {
            fontStyle: "italic",
          }),
      };
    }, []);

    const [imagePreview, setImagePreview] = useState({
      url: "",
      isLoading: false,
    });

    useEffect(() => {
      /**
       * Load preview image
       * - Checking is primary-imageUrl valid or not
       *   - If it is valid => Get that primary-imageUrl
       *   - If it is not valid => Maybe it's S3-image-key
       *    => Get S3-imageUrl from S3-image-key
       *    - Continue checking is S3-imageUrl from S3-image-key valid or not
       *    - If it is valid => Get the S3-imageUrl
       *    - If it is not valid => Get the primary-imageUrl
       */
      const loadPreviewImage = async (imageUrl) => {
        try {
          await validateUrlImage({ url: imageUrl });

          setImagePreview({ url: imageUrl, isLoading: false });
        } catch (e) {
          console.log("Not validate image", e);
          setImagePreview({ url: "", isLoading: true });
          try {
            const url = await Storage.get(imageUrl);
            await validateUrlImage({ url });

            setImagePreview({ url, isLoading: false });
          } catch (err) {
            console.log("Get image S3 error", err);
            setImagePreview({ url: imageUrl, isLoading: false });
          }
        }
      };

      if (imageUrl) {
        loadPreviewImage(imageUrl);
        return;
      }

      // If don't have imageUrl -> Set image empty
      setImagePreview({ url: "", isLoading: false });
    }, [imageUrl]);

    const onChangeImage = async (e) => {
      e.preventDefault();

      let uploadImage = e.target.files[0];

      if (!uploadImage) return;

      let reader = new FileReader();
      reader.onload = () => {
        setImagePreview({ url: "", isLoading: true });
      };
      reader.onloadend = () => {
        setImagePreview({ url: reader.result, isLoading: true });
      };

      reader.readAsDataURL(uploadImage);

      // Send the uploadImage file to parent
      handleChangeImage(menuItemIndex, uploadImage);
    };

    const onRemoveImage = () => {
      handleRemoveImage(menuItemIndex, imageUrl);
      setImagePreview({ url: "", isLoading: false });
    };

    return (
      <Card style={{ width: "100%" }} className="mt-2 mb-2">
        <div className="preview-menu-image">
          {imagePreview.url ? (
            <Card.Img variant="top" src={imagePreview.url} />
          ) : (
            <p className="message">
              {imagePreview.isLoading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="isProcessing"
                  className="ml-1"
                  aria-hidden="true"
                />
              ) : (
                "Upload your image here"
              )}
            </p>
          )}
          <div className="card-action">
            <label className="card-icon-wrapper">
              <EditIcon className="upload-image-icon" />
              <input
                accept="image/*"
                className="input"
                type="file"
                onChange={onChangeImage}
                data-testid="edit-preview-image"
              />
            </label>
            <label
              className={`card-icon-wrapper ${
                imagePreview.url ? "" : "disabled"
              }`}
              data-testid="remove-preview-image"
              onClick={imagePreview.url ? onRemoveImage : null}
            >
              <RemoveIcon className="remove-icon" />
            </label>
          </div>
        </div>
        <Card.Body>
          <Card.Title style={{ ...textStyle(normalText) }}>{name}</Card.Title>
          <div className="d-flex justify-content-between">
            <Card.Text
              style={{ ...textStyle(subtext) }}
            >{`${vintage} ${format}`}</Card.Text>
            <Card.Text
              className="font-italic"
              style={{ ...textStyle(normalText) }}
            >
              ${price}
            </Card.Text>
          </div>
        </Card.Body>
      </Card>
    );
  }
);

const PreviewMobileLayoutImages = ({
  jsonContent,
  handleChangeImage,
  handleRemoveImage,
  menuSetting,
}) => {
  const handleChangeImageCallback = React.useCallback(handleChangeImage, [
    handleChangeImage,
  ]);

  const handleRemoveImageCallback = React.useCallback(handleRemoveImage, [
    handleRemoveImage,
  ]);

  const { normalText, subtext } = menuSetting;

  return (
    <>
      {jsonContent && jsonContent.length ? (
        <div className="d-flex justify-content-center w-100 pt-2 pb-2 preview-menu-wrapper">
          <div className="preview-menu">
            {jsonContent.map((menuItem, index) => {
              const { name, price, format, vintage, imageUrl } = menuItem;
              return (
                <PreviewMenuCard
                  key={index}
                  menuItemIndex={index}
                  name={name}
                  price={price}
                  format={format}
                  vintage={vintage}
                  imageUrl={imageUrl}
                  handleChangeImage={handleChangeImageCallback}
                  handleRemoveImage={handleRemoveImageCallback}
                  normalText={normalText}
                  subtext={subtext}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
};

PreviewMobileLayoutImages.propTypes = {
  jsonContent: PropTypes.array.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleRemoveImage: PropTypes.func.isRequired,
  menuSetting: PropTypes.object.isRequired,
};

PreviewMenuCard.propTypes = {
  name: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  format: PropTypes.string.isRequired,
  vintage: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  menuItemIndex: PropTypes.number.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleRemoveImage: PropTypes.func.isRequired,
  normalText: PropTypes.object.isRequired,
  subtext: PropTypes.object.isRequired,
};

export default memo(PreviewMobileLayoutImages);
